import { useMemo } from 'react'
import {
    Column,
    ColumnInstance,
    Filters,
    IdType,
    SortingRule,
    TableOptions,
    TableState,
} from 'react-table'
import CellDefault from './CellDefault'
import { SpecTableProps } from './Spectable.types'
import FilterTextInput from './filters/FilterTextInput'

export interface AdditionalColumnProps {
    sticky?: 'left' | 'right'
    stickyRightMargin?: number
    stickyLeftMargin?: number
}

export interface UseSpecTableOptionProps<TData extends Object> {
    /***** REQUIRED PROPS *****/
    /**
     * The data that gets rendered
     */
    data: TData[]
    /**
     * The structure of the columns.  Anything set on columns will override the defaultColumn
     */
    columns: Column<TData>[]
    /**
     * Spectable must have a containing div to compute the max width of the table.
     * Pass the ref of the containing div to this prop.
     */
    divContainerRef: React.MutableRefObject<HTMLDivElement | undefined>
    /**
     * This is the event that gets triggered for new fetches.  Normally triggered by pageSize / pageIndex / sortBy / filters / and groupBy change.
     */
    fetchData: () => void
    /**
     * Loading state for fetch data.
     */
    fetchDataLoading: boolean

    /**
     * Total number of items.  This is not data.length but the total items in the database.
     */
    totalItems: number

    /***** EXTENDED TABLE  PROPS *****/
    /**
     * This is a global column settings.  Any column specific properties will override this object.
     * Very useful for setting up the minWidth, width, etc.
     * This defaults to have these properties on each column:
     *  - minWidth: 220
     *  - width: 300
     *  - Filter: default Textfield box
     */
    defaultColumn?: Column<TData>
    /**
     * The order of the columns. This will default to how you ordered your columns
     */
    columnOrder?: IdType<TData>[]
    /**
     * filters for columns
     */
    filters?: Filters<TData>
    /**
     * group by columns.  allowGrouping must be True.
     */
    groupBy?: IdType<TData>[]
    /**
     * Columns to hide
     */
    hiddenColumns?: IdType<TData>[]
    /**
     * Define the sortBy of the columns
     */
    sortBy?: SortingRule<TData>[]
    /**
     * Row count on each data load.
     */
    pageSize?: number
    /**
     * Sets the initial state of rows that should be expanded.
     */
    expanded?: Record<IdType<TData>, boolean>
    /**
     * Selected rows default
     */
    selectedRowIds?: Record<IdType<TData>, boolean>
    /**
     * When true, the expanded state will automatically reset when data is changed
     */
    autoResetExpanded?: boolean
    /**
     * For setting the custom minWidth of the table columns set to 220 by default
     */
    minWidth?: number
    /**
     * For setting the width of the groupcolexpander
     */
    groupColumnWidth?: number

    /*
     * Optional props so that the useSpectableOptions can be extended without adding more Prop declaration.
     * This will elimitate the error that will arise in time when reusing this to a component that doesnt need the props from another component
     */
    [key: string]: any
}

const useSpecTableOptions = <TData extends Object>({
    data,
    columns,
    divContainerRef,
    fetchData,
    fetchDataLoading,
    totalItems,
    defaultColumn,
    columnOrder = [],
    filters = [],
    groupBy = [],
    hiddenColumns = [],
    sortBy = [],
    pageSize,
    expanded = {} as any,
    selectedRowIds = {} as any,
    autoResetExpanded,
    minWidth = 200,
    groupColumnWidth = 300,
    ...options
}: UseSpecTableOptionProps<TData>) => {
    const finalizedDefaultColumn = useMemo(
        () =>
            ({
                Filter: FilterTextInput,
                Cell: CellDefault,
                minWidth,
                width: 300,
                ...defaultColumn,
            } as ColumnInstance<TData>),
        [defaultColumn]
    )

    let initialState: Partial<TableState<TData>> = {
        columnOrder,
        filters,
        groupBy,
        hiddenColumns,
        sortBy,
        pageSize,
        expanded,
        selectedRowIds,
    }

    const tableOptions: TableOptions<TData> = {
        data,
        columns,
        groupColumnWidth,
        defaultColumn: finalizedDefaultColumn,
        initialState,
        manualSortBy: true,
        manualFilters: true,
        manualGroupBy: false,
        isMultiSortEvent: () => true,
        autoResetExpanded,
    }

    return {
        tableOptions,
        divContainerRef,
        fetchData,
        fetchDataLoading,
        totalItems,
        ...options,
    } as SpecTableProps<TData>
}

export default useSpecTableOptions
